import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, ConfirmModal, CustomSelect, StatusDot, Amount, RowActions, RowAction, btnPrimary } from '@redpanda/ui';
import { formatTableDate, isAdmin } from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { deleteRequirement, deleteMilestone, deleteTask, deletePayment, deletePackage, reorderMilestones, updateProject, updateRequirement, updateMilestone, updateTask, updatePackage } from '../services/api';
import { useRequirementStore } from '../stores/pmRequirementStore';
import { useMilestoneStore } from '../stores/pmMilestoneStore';
import { useTaskStore } from '../stores/pmTaskStore';
import { usePaymentStore } from '../stores/pmPaymentStore';
import { usePackageStore } from '../stores/pmPackageStore';
import { toast } from 'react-hot-toast';
import RequirementDialog from '../components/RequirementDialog';
import MilestoneDialog from '../components/MilestoneDialog';
import TaskDialog from '../components/TaskDialog';
import PaymentDialog from '../components/PaymentDialog';
import PackageDialog from '../components/PackageDialog';
import ProjectDialog from '../components/ProjectDialog';
import ProjectHeader from '../components/ProjectHeader';
import AcceptanceLinkControl from '../components/AcceptanceLinkControl';
import BadDebtControl from '../components/BadDebtControl';
import RecurringBillingControl from '../components/RecurringBillingControl';
import TimeEntryDialog from '../components/TimeEntryDialog';
import ChangeOrderSection from '../components/ChangeOrderSection';
import ProjectTimePanel, { useProfitability } from '../components/ProjectTimePanel';
import EmptyPanel from '../components/EmptyPanel';
import FileList, { FileListRef } from '../components/FileList';
import ProjectExpensesPanel, { ProjectExpensesPanelRef } from '../components/ProjectExpensesPanel';
import { useProject } from '../hooks/useProject';
import { notifyDataChanged, DataTopic } from '../utils/dataEvents';
import { priorityTone, toneOf } from '../utils/statusTone';
import { isPastDue } from '../utils/dateUtils';
import { pendingRowClass } from '../utils/pendingItem';
import { PMRequirement, PMMilestone, PMTask, PMPayment, PMPackage } from '../types/pm';
import { getProjectStatusOptions, getHoldReasonOptions, getRequirementStatusOptions, getMilestoneStatusOptions, getTaskStatusOptions, getPackageStatusOptions } from '../utils/constants';
import { useMembers, useCrmCustomers, memberName, customerName } from '../stores/lookupStore';
import ProjectProgressBar, { computeProgress } from '../components/ProjectProgressBar';

type TabKey = 'overview' | 'packages' | 'requirements' | 'timeline' | 'tasks' | 'time' | 'payments' | 'expenses' | 'files';
const TABS: TabKey[] = ['overview', 'packages', 'requirements', 'timeline', 'tasks', 'time', 'payments', 'expenses', 'files'];
/** 只有管理員看得到的分頁（外部成本和毛利一樣只給管理員） */
const ADMIN_TABS: TabKey[] = ['expenses'];
type DeleteType = 'requirement' | 'milestone' | 'task' | 'payment' | 'package';

// topic：刪除後要通知的資料異動主題（需求、里程碑沒有訂閱者）
const deleteMeta: Record<DeleteType, { ns: string; topic?: DataTopic }> = {
  requirement: { ns: 'pmRequirements' },
  milestone: { ns: 'pmMilestones' },
  task: { ns: 'pmTasks', topic: 'tasks' },
  payment: { ns: 'pmPayments', topic: 'payments' },
  package: { ns: 'pmPackages', topic: 'packages' },
};

/** 空值一律「—」輔助色（§3-13） */
const Empty = () => <span className="text-fg-muted">—</span>;

/** 表格外框（照 CRM 列表） */
const TableFrame: React.FC<{ children: React.ReactNode; footer?: React.ReactNode }> = ({ children, footer }) => (
  <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
    <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
      {children}
    </div>
    {footer}
  </div>
);

const LoadingPanel = () => (
  <div className="h-48 rounded-lg border border-line bg-surface animate-pulse motion-reduce:animate-none" />
);

const ProjectDetailPage: React.FC = () => {
  const { t, i18n } = useTranslation(['translation', 'pages']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { project, loading, notFound, setProject } = useProject(id);
  const profitability = useProfitability(id ?? ''); // 管理員才有（成本費率只有管理員看得到）
  const { user } = useContext(UserContext)!;
  const members = useMembers();
  const customers = useCrmCustomers();

  const { items: allRequirements, update: updateRequirementInStore } = useRequirementStore();
  const { items: allMilestones, update: updateMilestoneInStore, setProjectItems: setProjectMilestones } = useMilestoneStore();
  const { items: allTasks, update: updateTaskInStore } = useTaskStore();
  const { items: allPayments } = usePaymentStore();
  const { items: allPackages, update: updatePackageInStore } = usePackageStore();

  // 分頁存在網址（?tab=），重新整理、從看板／甘特圖返回時停在同一頁
  const tabParam = searchParams.get('tab') as TabKey | null;
  const visibleTabs = isAdmin(user?.role) ? TABS : TABS.filter(tab => !ADMIN_TABS.includes(tab));
  const activeTab: TabKey = tabParam && visibleTabs.includes(tabParam) ? tabParam : 'overview';
  const setActiveTab = (tab: TabKey) => setSearchParams(tab === 'overview' ? {} : { tab }, { replace: true });

  const requirements = useMemo(() => allRequirements.filter(r => r.projectId === id), [allRequirements, id]);
  const milestones = useMemo(() => allMilestones.filter(m => m.projectId === id), [allMilestones, id]);
  const tasks = useMemo(() => allTasks.filter(task => task.projectId === id), [allTasks, id]);
  const payments = useMemo(() => allPayments.filter(p => p.projectId === id), [allPayments, id]);
  const packages = useMemo(
    () => allPackages.filter(p => p.projectId === id).sort((a, b) => a.orderIndex - b.orderIndex),
    [allPackages, id]
  );

  // 分包合計（已取消的包不計入）
  const packageTotals = useMemo(() => {
    const active = packages.filter(p => p.status !== 'CANCELLED');
    const amount = active.reduce((sum, p) => sum + (p.amount || 0), 0);
    const paid = active.reduce((sum, p) => sum + (p.paidAmount || 0), 0);
    return { amount, paid, unpaid: Math.max(0, amount - paid) };
  }, [packages]);

  // 目前開啟的對話框；item 為編輯對象（沒有＝新增）
  const [dialog, setDialog] = useState<
    | { type: 'project' }
    | { type: 'package'; item?: PMPackage }
    | { type: 'requirement'; item?: PMRequirement }
    | { type: 'milestone'; item?: PMMilestone }
    | { type: 'task'; item?: PMTask }
    | { type: 'payment'; item?: PMPayment }
    | { type: 'time' }
    | null
  >(null);
  const [deleteModal, setDeleteModal] = useState<{ id: string; type: DeleteType } | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [paymentSort, setPaymentSort] = useState<{ by: 'date' | 'amount'; order: 'asc' | 'desc' }>({ by: 'date', order: 'desc' });
  const fileListRef = React.useRef<FileListRef>(null);
  const expensesRef = React.useRef<ProjectExpensesPanelRef>(null);

  // 子資料（需求、分包、里程碑、任務、收款）由 useProject 一次抓回並填入各 store：有快取先顯示、背景重抓（R31）
  // 收款、分包異動（影響已收款、分包關聯）時 useProject 會整包重抓，這裡不用另外訂閱

  // 通知連結 ?task=<id>：打開該任務（資料載入後），網址留在任務分頁
  const taskParam = searchParams.get('task');
  useEffect(() => {
    if (!taskParam) return;
    const target = tasks.find(task => task.id === taskParam);
    if (!target) return;
    setDialog({ type: 'task', item: target });
    setSearchParams({ tab: 'tasks' }, { replace: true });
  }, [taskParam, tasks, setSearchParams]);

  useEffect(() => {
    if (notFound) {
      toast.error(t('pmProjects.loadError', { ns: 'pages' }));
      navigate('/projects');
    }
  }, [notFound, navigate, t]);

  // 狀態下拉（樂觀更新，失敗回滾）
  const changeStatus = async (
    itemId: string,
    previous: string,
    next: string,
    applyLocal: (status: string) => void,
    request: () => Promise<unknown>,
    topic?: DataTopic
  ) => {
    applyLocal(next);
    setUpdatingId(itemId);
    try {
      await request();
      toast.success(t('common.messages.updateSuccess'));
      if (topic) notifyDataChanged(topic);
    } catch (error: any) {
      console.error('Failed to update status:', error);
      applyLocal(previous);
      toast.error(error.response?.data?.error || t('common.messages.updateFailed'));
    } finally {
      setUpdatingId(null);
    }
  };

  const handleProjectStatusChange = (newStatus: string) => {
    if (!project) return;
    // 改成已完成且尚未填實際交付日期時，自動填入今天
    const payload: Record<string, string | null> = { status: newStatus };
    if (newStatus === 'COMPLETED' && !project.actualDeliveryDate) payload.actualDeliveryDate = new Date().toISOString();
    if (newStatus !== 'ON_HOLD') payload.holdReason = null; // 暫停原因只在暫停中有意義（後端也會清）
    const original = project;
    changeStatus(
      project.id, project.status, newStatus,
      status => setProject(status === newStatus ? { ...original, ...payload } : original),
      () => updateProject(project.id, payload),
      'projects'
    );
  };

  // 暫停原因（例：客戶無回應），樂觀更新
  const handleHoldReasonChange = (holdReason: string) => {
    if (!project) return;
    const original = project;
    changeStatus(
      `${project.id}-hold`, project.holdReason ?? '', holdReason,
      value => setProject({ ...original, holdReason: value || null }),
      () => updateProject(project.id, { holdReason }),
      'projects'
    );
  };

  const handleCompleteMilestone = async (milestoneId: string) => {
    try {
      // 後端改成已完成時會記下完成日期
      const response = await updateMilestone(milestoneId, { status: 'COMPLETED' });
      updateMilestoneInStore(milestoneId, response.data);
      toast.success(t('pmMilestones.completeSuccess', { ns: 'pages' }));
    } catch (error) {
      console.error('Failed to complete milestone:', error);
      toast.error(t('pmMilestones.completeError', { ns: 'pages' }));
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    const { id: itemId, type } = deleteModal;
    setDeleteModal(null);

    const stores = {
      requirement: () => useRequirementStore.getState().optimisticDelete(itemId, deleteRequirement),
      milestone: () => useMilestoneStore.getState().optimisticDelete(itemId, deleteMilestone),
      task: () => useTaskStore.getState().optimisticDelete(itemId, deleteTask),
      payment: () => usePaymentStore.getState().optimisticDelete(itemId, deletePayment),
      package: () => usePackageStore.getState().optimisticDelete(itemId, deletePackage),
    };
    const { ns, topic } = deleteMeta[type];
    try {
      await stores[type]();
      toast.success(t(`${ns}.deleteSuccess`, { ns: 'pages' }));
      if (topic) notifyDataChanged(topic);
    } catch (error) {
      console.error(`Failed to delete ${type}:`, error);
      toast.error(t(`${ns}.deleteError`, { ns: 'pages' }));
    }
  };

  const handleMilestoneDragEnd = async (result: DropResult) => {
    if (!result.destination || !id || result.source.index === result.destination.index) return;

    const reordered = Array.from(milestones);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    const withIndex = reordered.map((milestone, index) => ({ ...milestone, orderIndex: index }));

    setProjectMilestones(id, withIndex);
    try {
      await reorderMilestones(id, { milestones: withIndex.map(m => ({ id: m.id, orderIndex: m.orderIndex })) });
      toast.success(t('common.messages.updateSuccess'));
    } catch (error) {
      console.error('Failed to reorder milestones:', error);
      toast.error(t('common.messages.updateFailed'));
      setProjectMilestones(id, milestones); // 回到拖曳前的順序
    }
  };

  const closeDialog = () => setDialog(null);

  const sortedPayments = useMemo(() => {
    const list = [...payments].sort((a, b) =>
      paymentSort.by === 'date'
        ? new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime()
        : (a.amount || 0) - (b.amount || 0)
    );
    return paymentSort.order === 'desc' ? list.reverse() : list;
  }, [payments, paymentSort]);

  const sortHeader = (by: 'date' | 'amount', label: string) => {
    const active = paymentSort.by === by;
    return (
      <button
        type="button"
        onClick={() => setPaymentSort(prev => ({ by, order: prev.by === by && prev.order === 'desc' ? 'asc' : 'desc' }))}
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-fg ${active ? 'text-fg' : ''}`}
        aria-sort={active ? (paymentSort.order === 'desc' ? 'descending' : 'ascending') : undefined}
      >
        {label}
        {active && <span aria-hidden="true">{paymentSort.order === 'desc' ? '↓' : '↑'}</span>}
      </button>
    );
  };

  const date = (value?: string | null) => (value ? <span className="tabular-nums">{formatTableDate(value, i18n.language)}</span> : <Empty />);
  const statusSelect = (value: string, options: { value: string; label: string }[], onChange: (v: string) => void, itemId: string) => (
    <div className="w-28">
      <CustomSelect value={value} onChange={onChange} disabled={updatingId === itemId} options={options} className="text-sm" />
    </div>
  );

  if (loading || !project || !id) {
    return (
      <div className="container mx-auto px-10 py-6 pb-32">
        <div className="h-4 w-16 mb-2 rounded bg-slate-200 dark:bg-slate-800" />
        <div className="h-8 w-64 mb-4 rounded bg-slate-200 dark:bg-slate-800" />
        <LoadingPanel />
      </div>
    );
  }

  // 標題列右側的主要動作隨分頁切換（每個畫面只有一顆主要按鈕）
  const tabActions: Record<TabKey, { label: string; onClick: () => void }> = {
    overview: { label: t('pmProjects.editTitle', { ns: 'pages' }), onClick: () => setDialog({ type: 'project' }) },
    packages: { label: t('pmPackages.addButton', { ns: 'pages' }), onClick: () => setDialog({ type: 'package' }) },
    requirements: { label: t('pmRequirements.addButton', { ns: 'pages' }), onClick: () => setDialog({ type: 'requirement' }) },
    timeline: { label: t('pmMilestones.addButton', { ns: 'pages' }), onClick: () => setDialog({ type: 'milestone' }) },
    tasks: { label: t('pmTasks.addButton', { ns: 'pages' }), onClick: () => setDialog({ type: 'task' }) },
    time: { label: t('pmTime.addButton', { ns: 'pages' }), onClick: () => setDialog({ type: 'time' }) },
    payments: { label: t('pmPayments.addButton', { ns: 'pages' }), onClick: () => setDialog({ type: 'payment' }) },
    expenses: { label: t('pmExpenses.addButton', { ns: 'pages' }), onClick: () => expensesRef.current?.openCreateDialog() },
    files: { label: t('pages:pm.files.uploadFile'), onClick: () => fileListRef.current?.openUploadDialog() },
  };
  const primaryAction = (
    <button type="button" onClick={tabActions[activeTab].onClick} className={btnPrimary}>
      {tabActions[activeTab].label}
    </button>
  );

  const facts: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: t('pmProjects.fields.status', { ns: 'pages' }),
      value: statusSelect(project.status, getProjectStatusOptions(t), handleProjectStatusChange, project.id),
    },
    // 暫停中才顯示：註明為什麼停（客戶無回應、等待資料…），列表滑過狀態也看得到
    ...(project.status === 'ON_HOLD' ? [{
      label: t('pmProjects.holdReason.label', { ns: 'pages' }),
      value: (
        <div className="w-40">
          <CustomSelect
            value={project.holdReason ?? ''}
            onChange={handleHoldReasonChange}
            disabled={updatingId === `${project.id}-hold`}
            options={getHoldReasonOptions(t)}
            placeholder={t('pmProjects.holdReason.none', { ns: 'pages' })}
            className="text-sm"
          />
        </div>
      ),
    }] : []),
    { label: t('pmProjects.progress.label', { ns: 'pages' }), value: <ProjectProgressBar progress={computeProgress(tasks, milestones)} /> },
    {
      label: t('pmProjects.fields.projectType', { ns: 'pages' }),
      value: project.projectType ? t(`pmProjects.projectTypes.${project.projectType}`, { ns: 'pages' }) : <Empty />,
    },
    { label: t('pmProjects.fields.customer', { ns: 'pages' }), value: project.customerId ? customerName(customers, project.customerId) : <Empty /> },
    { label: t('pmProjects.fields.consultingStartDate', { ns: 'pages' }), value: date(project.consultingStartDate) },
    { label: t('pmProjects.fields.consultingEndDate', { ns: 'pages' }), value: date(project.consultingEndDate) },
    { label: t('pmProjects.fields.expectedDeliveryDate', { ns: 'pages' }), value: date(project.expectedDeliveryDate) },
    { label: t('pmProjects.fields.actualDeliveryDate', { ns: 'pages' }), value: date(project.actualDeliveryDate) },
    ...(project.crmQuoteId ? [{ label: t('pmProjects.fields.crmQuoteId', { ns: 'pages' }), value: project.crmQuoteId }] : []),
    {
      label: t('pmProjects.acceptanceLink.label', { ns: 'pages' }),
      value: <AcceptanceLinkControl projectId={project.id} shareToken={project.shareToken} onChange={shareToken => setProject({ ...project, shareToken })} />,
    },
    { label: t('pmProjects.systemFields.createdAt', { ns: 'pages' }), value: date(project.createdAt) },
    { label: t('pmProjects.systemFields.updatedAt', { ns: 'pages' }), value: date(project.updatedAt) },
  ];

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <ProjectHeader projectId={id} project={project} current="detail" action={primaryAction} returnTab={activeTab === 'overview' ? undefined : activeTab} />

      {/* 分頁籤（底線式；與其他頁搜尋列同高 h-10） */}
      <nav className="mb-4 flex gap-6 overflow-x-auto scrollbar-hide border-b border-line" aria-label={t('pmProjects.overview', { ns: 'pages' })}>
        {visibleTabs.map(tab => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            aria-current={activeTab === tab ? 'page' : undefined}
            className={`h-10 -mb-px shrink-0 border-b-2 text-sm transition-colors ${
              activeTab === tab ? 'border-accent text-fg font-medium' : 'border-transparent text-fg-secondary hover:text-fg'
            }`}
          >
            {t(`pmProjects.tabs.${tab}`, { ns: 'pages' })}
          </button>
        ))}
      </nav>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左欄：「標籤：值」清單（R20） */}
          <dl className="lg:col-span-1 self-start bg-surface rounded-lg border border-line divide-y divide-line text-sm">
            {facts.map(f => (
              <div key={f.label} className="grid grid-cols-[7rem_1fr] items-center gap-3 px-5 py-3">
                <dt className="text-fg-secondary">{f.label}</dt>
                <dd className="text-fg min-w-0 break-words">{f.value}</dd>
              </div>
            ))}
          </dl>

          {/* 右欄：財務＋說明，一個容器、細線分隔 */}
          <div className="lg:col-span-2 self-start bg-surface rounded-lg border border-line divide-y divide-line">
            <section className="px-5 py-4">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-base font-semibold text-fg">{t('pmProjects.sections.financialInfo', { ns: 'pages' })}</h2>
                {packages.length > 0 && (
                  <button type="button" onClick={() => setActiveTab('packages')} className="text-sm text-accent hover:underline">
                    {t('pmPackages.summaryLink', { ns: 'pages', count: packages.length })}
                  </button>
                )}
              </div>
              <dl className="grid grid-cols-3 gap-4">
                {[
                  { label: t('pmProjects.fields.totalBudget', { ns: 'pages' }), value: project.totalBudget },
                  { label: t('pmProjects.fields.paidAmount', { ns: 'pages' }), value: project.paidAmount ?? 0 },
                  { label: t('pmProjects.fields.pendingAmount', { ns: 'pages' }), value: project.pendingAmount ?? 0 },
                ].map(item => (
                  <div key={item.label}>
                    <dt className="text-[13px] text-fg-secondary">{item.label}</dt>
                    <dd className="mt-1 text-xl font-semibold text-fg tabular-nums whitespace-nowrap">
                      {item.value === null || item.value === undefined ? <Empty /> : <Amount value={item.value} />}
                    </dd>
                  </div>
                ))}
              </dl>
              {profitability && (
                <dl className="mt-4 pt-4 border-t border-line grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <dt className="text-[13px] text-fg-secondary">{t('pmTime.profit.laborCost', { ns: 'pages' })}</dt>
                    <dd className="mt-1 text-xl font-semibold text-fg tabular-nums whitespace-nowrap"><Amount value={profitability.laborCost} /></dd>
                  </div>
                  <div>
                    <dt className="text-[13px] text-fg-secondary">{t('pmExpenses.summary.expenses', { ns: 'pages' })}</dt>
                    <dd className="mt-1 text-xl font-semibold text-fg tabular-nums whitespace-nowrap"><Amount value={profitability.expenseCost ?? 0} /></dd>
                  </div>
                  <div>
                    <dt className="text-[13px] text-fg-secondary">{t('pmTime.profit.margin', { ns: 'pages' })}</dt>
                    <dd className={`mt-1 text-xl font-semibold tabular-nums whitespace-nowrap ${profitability.margin < 0 ? 'text-red-600 dark:text-red-400' : 'text-fg'}`}>
                      <Amount value={profitability.margin} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[13px] text-fg-secondary">{t('pmTime.profit.marginRate', { ns: 'pages' })}</dt>
                    <dd className="mt-1 text-xl font-semibold text-fg tabular-nums whitespace-nowrap">
                      {profitability.marginRate === null ? <Empty /> : `${profitability.marginRate}%`}
                    </dd>
                  </div>
                </dl>
              )}
              {/* 成本預算用量（有設預算才顯示）：80% 以上警示、100% 以上超支 */}
              {profitability?.costUsage != null && (
                <button
                  type="button"
                  onClick={() => setActiveTab('expenses')}
                  className={`mt-3 text-[13px] hover:underline ${
                    profitability.costUsage >= 100 ? 'text-red-600 dark:text-red-400' : profitability.costUsage >= 80 ? 'text-amber-700 dark:text-amber-400' : 'text-fg-secondary'
                  }`}
                >
                  {t('pmExpenses.budget.usageLine', { ns: 'pages', usage: profitability.costUsage, budget: (profitability.costBudget ?? 0).toLocaleString() })}
                </button>
              )}
              <RecurringBillingControl project={project} canEdit={isAdmin(user?.role)} onChange={setProject} />
              <BadDebtControl project={project} canEdit={isAdmin(user?.role)} onChange={setProject} />
            </section>
            <section className="px-5 py-4">
              <h2 className="text-base font-semibold text-fg mb-2">{t('pmProjects.fields.description', { ns: 'pages' })}</h2>
              {project.description
                ? <p className="text-sm text-fg-secondary whitespace-pre-wrap">{project.description}</p>
                : <p className="text-sm text-fg-muted">—</p>}
            </section>
          </div>
        </div>
      )}

      {activeTab === 'packages' && (
        <>
        {packages.length === 0 ? (
          <EmptyPanel title={t('pmPackages.noDataTitle', { ns: 'pages' })} hint={t('pmPackages.noData', { ns: 'pages' })} action={primaryAction} />
        ) : (
          <TableFrame footer={<p className="px-6 py-3 text-[13px] text-fg-secondary border-t border-line">{t('pmPackages.hint', { ns: 'pages' })}</p>}>
            <Table style={{ tableLayout: 'fixed', minWidth: '820px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">{t('pmPackages.fields.orderIndex', { ns: 'pages' })}</TableHead>
                  <TableHead>{t('pmPackages.fields.name', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-32">{t('pmPackages.fields.amount', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-32">{t('pmPackages.fields.paidAmount', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-32">{t('pmPackages.fields.unpaidAmount', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-36">{t('pmPackages.fields.status', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-28">{t('common.table.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {packages.map((pkg, index) => {
                  const isCancelled = pkg.status === 'CANCELLED';
                  const paid = pkg.paidAmount || 0;
                  return (
                    <TableRow key={pkg.id} className={`${isCancelled ? 'opacity-60' : ''} ${pendingRowClass(pkg)}`}>
                      <TableCell className="tabular-nums">{index + 1}</TableCell>
                      <TableCell className="whitespace-normal">
                        <div className="font-medium text-fg">{pkg.name}</div>
                        {pkg.description && <div className="mt-0.5 text-[13px] text-fg-secondary line-clamp-1">{pkg.description}</div>}
                        {pkg.crmQuoteId && <div className="mt-0.5 text-[13px] text-fg-secondary">{t('pmPackages.fields.crmQuoteId', { ns: 'pages' })}: {pkg.crmQuoteId}</div>}
                      </TableCell>
                      <TableCell numeric className={isCancelled ? 'line-through' : ''}><Amount value={pkg.amount || 0} /></TableCell>
                      <TableCell numeric><Amount value={paid} /></TableCell>
                      <TableCell numeric>{isCancelled ? <Empty /> : <Amount value={Math.max(0, (pkg.amount || 0) - paid)} />}</TableCell>
                      <TableCell>
                        {statusSelect(pkg.status, getPackageStatusOptions(t), status => changeStatus(
                          pkg.id, pkg.status, status,
                          s => updatePackageInStore(pkg.id, { status: s }),
                          () => updatePackage(pkg.id, { status }),
                          'packages'
                        ), pkg.id)}
                      </TableCell>
                      <TableCell>
                        <RowActions>
                          <RowAction onClick={() => setDialog({ type: 'package', item: pkg })}>{t('common.actions.edit')}</RowAction>
                          <RowAction tone="danger" onClick={() => setDeleteModal({ id: pkg.id, type: 'package' })}>{t('common.actions.delete')}</RowAction>
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* 合計列 */}
                <TableRow className="bg-slate-50 dark:bg-slate-900/40">
                  <TableCell />
                  <TableCell className="font-semibold text-fg">{t('pmPackages.total', { ns: 'pages' })}</TableCell>
                  <TableCell numeric className="font-semibold"><Amount value={packageTotals.amount} /></TableCell>
                  <TableCell numeric className="font-semibold"><Amount value={packageTotals.paid} /></TableCell>
                  <TableCell numeric className="font-semibold"><Amount value={packageTotals.unpaid} /></TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableBody>
            </Table>
          </TableFrame>
        )}
        <ChangeOrderSection projectId={id} hasShareLink={!!project.shareToken} />
        </>
      )}

      {activeTab === 'requirements' && (
        requirements.length === 0 ? (
          <EmptyPanel title={t('pmRequirements.noData', { ns: 'pages' })} hint={t('pmRequirements.emptyHint', { ns: 'pages' })} action={primaryAction} />
        ) : (
          <TableFrame>
            <Table style={{ tableLayout: 'fixed', minWidth: '760px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('pmRequirements.fields.title', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-24">{t('pmRequirements.fields.priority', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-36">{t('pmRequirements.fields.category', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-24">{t('pmRequirements.fields.tasks', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-36">{t('pmRequirements.fields.status', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-28">{t('common.table.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requirements.map(req => (
                  <TableRow key={req.id} className={pendingRowClass(req)}>
                    <TableCell className="whitespace-normal">
                      <div className="font-medium text-fg">{req.title}</div>
                      {req.description && <div className="mt-0.5 text-[13px] text-fg-secondary line-clamp-1">{req.description}</div>}
                    </TableCell>
                    <TableCell>
                      <StatusDot tone={toneOf(priorityTone, req.priority)} label={t(`pmRequirements.priority.${req.priority}`, { ns: 'pages' })} />
                    </TableCell>
                    <TableCell className="truncate">{req.category || <Empty />}</TableCell>
                    <TableCell numeric>
                      {/* 實作這項需求的任務：完成數／任務數 */}
                      {(() => {
                        const linked = tasks.filter(task => task.requirementId === req.id);
                        return linked.length ? <span className="tabular-nums">{linked.filter(task => task.status === 'COMPLETED').length}/{linked.length}</span> : <Empty />;
                      })()}
                    </TableCell>
                    <TableCell>
                      {statusSelect(req.status, getRequirementStatusOptions(t), status => changeStatus(
                        req.id, req.status, status,
                        s => updateRequirementInStore(req.id, { status: s }),
                        () => updateRequirement(req.id, { status })
                      ), req.id)}
                    </TableCell>
                    <TableCell>
                      <RowActions>
                        <RowAction onClick={() => setDialog({ type: 'requirement', item: req })}>{t('common.actions.edit')}</RowAction>
                        <RowAction tone="danger" onClick={() => setDeleteModal({ id: req.id, type: 'requirement' })}>{t('common.actions.delete')}</RowAction>
                      </RowActions>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableFrame>
        )
      )}

      {activeTab === 'timeline' && (
        milestones.length === 0 ? (
          <EmptyPanel title={t('pmMilestones.noData', { ns: 'pages' })} hint={t('pmMilestones.emptyHint', { ns: 'pages' })} action={primaryAction} />
        ) : (
          <DragDropContext onDragEnd={handleMilestoneDragEnd}>
            <TableFrame>
              <Table style={{ tableLayout: 'fixed', minWidth: '900px' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"><span className="sr-only">{t('pmMilestones.dragHandle', { ns: 'pages' })}</span></TableHead>
                    <TableHead className="w-20">{t('pmMilestones.fields.orderIndex', { ns: 'pages' })}</TableHead>
                    <TableHead>{t('pmMilestones.fields.name', { ns: 'pages' })}</TableHead>
                    {packages.length > 0 && <TableHead className="w-32">{t('pmMilestones.fields.package', { ns: 'pages' })}</TableHead>}
                    <TableHead numeric className="w-24">{t('pmMilestones.fields.dueDate', { ns: 'pages' })}</TableHead>
                    <TableHead numeric className="w-36">{t('pmMilestones.fields.paymentPercentage', { ns: 'pages' })}</TableHead>
                    <TableHead numeric className="w-32">{t('pmMilestones.fields.paymentAmount', { ns: 'pages' })}</TableHead>
                    <TableHead className="w-36">{t('pmMilestones.fields.status', { ns: 'pages' })}</TableHead>
                    <TableHead className="w-36">{t('common.table.columns.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <Droppable droppableId="milestones">
                  {provided => (
                    <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                      {milestones.map((milestone, index) => (
                        <Draggable key={milestone.id} draggableId={milestone.id} index={index} isDragDisabled={milestone.id.startsWith('temp-')}>
                          {(dragProvided, snapshot) => (
                            <TableRow
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              className={`${snapshot.isDragging ? 'bg-slate-100 dark:bg-slate-700' : ''} ${pendingRowClass(milestone)}`}
                            >
                              <TableCell {...dragProvided.dragHandleProps} className="cursor-move">
                                <svg className="w-5 h-5 text-fg-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                                </svg>
                              </TableCell>
                              <TableCell className="tabular-nums">{milestone.orderIndex + 1}</TableCell>
                              <TableCell className="whitespace-normal">
                                <div className="font-medium text-fg">{milestone.name}</div>
                                {milestone.description && <div className="mt-0.5 text-[13px] text-fg-secondary line-clamp-1">{milestone.description}</div>}
                              </TableCell>
                              {packages.length > 0 && (
                                <TableCell className="truncate">{packages.find(p => p.id === milestone.packageId)?.name || <Empty />}</TableCell>
                              )}
                              <TableCell numeric>{date(milestone.dueDate)}</TableCell>
                              <TableCell numeric>{milestone.paymentPercentage ? `${milestone.paymentPercentage}%` : <Empty />}</TableCell>
                              <TableCell numeric>{milestone.paymentAmount ? <Amount value={milestone.paymentAmount} /> : <Empty />}</TableCell>
                              <TableCell>
                                {statusSelect(milestone.status, getMilestoneStatusOptions(t), status => changeStatus(
                                  milestone.id, milestone.status, status,
                                  s => updateMilestoneInStore(milestone.id, { status: s }),
                                  () => updateMilestone(milestone.id, { status })
                                ), milestone.id)}
                                {milestone.acceptanceStatus && (
                                  <div
                                    className={`mt-1 text-[13px] ${milestone.acceptanceStatus === 'ACCEPTED' ? 'text-fg-secondary' : 'text-amber-700 dark:text-amber-400'}`}
                                    title={milestone.acceptanceNote || undefined}
                                  >
                                    {t(`pmMilestones.acceptance.${milestone.acceptanceStatus}`, { ns: 'pages', name: milestone.acceptanceBy })}
                                    {milestone.acceptanceNote && <span className="block truncate">「{milestone.acceptanceNote}」</span>}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <RowActions>
                                  <RowAction onClick={() => setDialog({ type: 'milestone', item: milestone })}>{t('common.actions.edit')}</RowAction>
                                  {milestone.status !== 'COMPLETED' && (
                                    <RowAction onClick={() => handleCompleteMilestone(milestone.id)}>{t('pmMilestones.complete', { ns: 'pages' })}</RowAction>
                                  )}
                                  <RowAction tone="danger" onClick={() => setDeleteModal({ id: milestone.id, type: 'milestone' })}>{t('common.actions.delete')}</RowAction>
                                </RowActions>
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </Table>
            </TableFrame>
          </DragDropContext>
        )
      )}

      {activeTab === 'tasks' && (
        tasks.length === 0 ? (
          <EmptyPanel title={t('pmTasks.noData', { ns: 'pages' })} hint={t('pmTasks.emptyHint', { ns: 'pages' })} action={primaryAction} />
        ) : (
          <TableFrame>
            <Table style={{ tableLayout: 'fixed', minWidth: '860px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('pmTasks.fields.title', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-32">{t('pmTasks.fields.assignedTo', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-28">{t('pmTasks.fields.dueDate', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-24">{t('pmTasks.fields.priority', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-36">{t('pmTasks.fields.status', { ns: 'pages' })}</TableHead>
                  <TableHead numeric className="w-48">{t('pmTasks.fields.hours', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-28">{t('common.table.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map(task => {
                  const overdue = task.status !== 'COMPLETED' && isPastDue(task.dueDate);
                  return (
                    <TableRow key={task.id} className={pendingRowClass(task)}>
                      <TableCell className="whitespace-normal">
                        <div className="font-medium text-fg">{task.title}</div>
                        {task.description && <div className="mt-0.5 text-[13px] text-fg-secondary line-clamp-1">{task.description}</div>}
                      </TableCell>
                      <TableCell className="truncate">{task.assignedTo ? memberName(members, task.assignedTo) : <Empty />}</TableCell>
                      <TableCell numeric>
                        {task.dueDate ? (
                          <span className={overdue ? 'text-red-600 dark:text-red-400' : ''}>
                            {formatTableDate(task.dueDate, i18n.language)}
                            {overdue && <span className="sr-only"> {t('pmTasks.overdue', { ns: 'pages' })}</span>}
                          </span>
                        ) : <Empty />}
                      </TableCell>
                      <TableCell>
                        <StatusDot tone={toneOf(priorityTone, task.priority)} label={t(`pmTasks.priority.${task.priority}`, { ns: 'pages' })} />
                      </TableCell>
                      <TableCell>
                        {statusSelect(task.status, getTaskStatusOptions(t), status => changeStatus(
                          task.id, task.status, status,
                          s => updateTaskInStore(task.id, { status: s }),
                          () => updateTask(task.id, { status }),
                          'tasks'
                        ), task.id)}
                      </TableCell>
                      <TableCell numeric>
                        {task.estimatedHours ? `${task.estimatedHours}h` : <Empty />}
                        {task.actualHours ? <span className="text-fg-secondary"> / {task.actualHours}h</span> : null}
                      </TableCell>
                      <TableCell>
                        <RowActions>
                          <RowAction onClick={() => setDialog({ type: 'task', item: task })}>{t('common.actions.edit')}</RowAction>
                          <RowAction tone="danger" onClick={() => setDeleteModal({ id: task.id, type: 'task' })}>{t('common.actions.delete')}</RowAction>
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableFrame>
        )
      )}

      {activeTab === 'payments' && (
        payments.length === 0 ? (
          <EmptyPanel title={t('pmPayments.noData', { ns: 'pages' })} hint={t('pmPayments.emptyHint', { ns: 'pages' })} action={primaryAction} />
        ) : (
          <TableFrame>
            <Table style={{ tableLayout: 'fixed', minWidth: '760px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead numeric className="w-36">{sortHeader('date', t('pmPayments.fields.paymentDate', { ns: 'pages' }))}</TableHead>
                  <TableHead numeric className="w-32">{sortHeader('amount', t('pmPayments.fields.amount', { ns: 'pages' }))}</TableHead>
                  <TableHead className="w-28">{t('pmPayments.fields.paymentMethod', { ns: 'pages' })}</TableHead>
                  <TableHead>{t('pmPayments.fields.milestone', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-36">{t('pmPayments.fields.invoiceNumber', { ns: 'pages' })}</TableHead>
                  <TableHead className="w-28">{t('common.table.columns.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedPayments.map(payment => {
                  const milestone = milestones.find(m => m.id === payment.milestoneId);
                  return (
                    <TableRow key={payment.id} className={pendingRowClass(payment)}>
                      <TableCell numeric>{date(payment.paymentDate)}</TableCell>
                      <TableCell numeric>
                        <Amount value={payment.amount || 0} />
                        {!!payment.adjustmentAmount && payment.adjustmentType && (
                          <div className="mt-0.5 text-[13px] text-fg-secondary">
                            {t('pmPayments.adjustmentLine', { ns: 'pages', type: t(`pmPayments.adjustmentType.${payment.adjustmentType}`, { ns: 'pages' }), amount: payment.adjustmentAmount.toLocaleString() })}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{t(`pmPayments.paymentMethod.${payment.paymentMethod}`, { ns: 'pages' })}</TableCell>
                      <TableCell className="whitespace-normal">
                        <div>{milestone ? milestone.name : <Empty />}</div>
                        {payment.packageId && packages.length > 1 && (
                          <div className="mt-0.5 text-[13px] text-fg-secondary">{packages.find(p => p.id === payment.packageId)?.name}</div>
                        )}
                      </TableCell>
                      <TableCell className="truncate tabular-nums">{payment.invoiceNumber || <Empty />}</TableCell>
                      <TableCell>
                        <RowActions>
                          <RowAction onClick={() => setDialog({ type: 'payment', item: payment })}>{t('common.actions.edit')}</RowAction>
                          <RowAction tone="danger" onClick={() => setDeleteModal({ id: payment.id, type: 'payment' })}>{t('common.actions.delete')}</RowAction>
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableFrame>
        )
      )}

      {activeTab === 'time' && <ProjectTimePanel projectId={id} />}

      {activeTab === 'expenses' && <ProjectExpensesPanel ref={expensesRef} projectId={id} packages={packages} />}

      {activeTab === 'files' && <FileList ref={fileListRef} projectId={id} />}

      {/* 對話框 */}
      {dialog?.type === 'requirement' && (
        <RequirementDialog isOpen onClose={closeDialog} projectId={id} requirement={dialog.item} />
      )}
      {dialog?.type === 'milestone' && (
        <MilestoneDialog
          isOpen
          onClose={closeDialog}
          projectId={id}
          milestone={dialog.item}
          nextOrderIndex={milestones.length}
          totalBudget={project.totalBudget}
          packages={packages}
        />
      )}
      {dialog?.type === 'package' && (
        <PackageDialog isOpen onClose={closeDialog} projectId={id} pkg={dialog.item} nextPackageNumber={packages.length + 1} />
      )}
      {dialog?.type === 'task' && (
        <TaskDialog isOpen onClose={closeDialog} projectId={id} task={dialog.item} milestones={milestones} />
      )}
      {dialog?.type === 'payment' && (
        <PaymentDialog isOpen onClose={closeDialog} projectId={id} payment={dialog.item} milestones={milestones} packages={packages} />
      )}
      {dialog?.type === 'time' && <TimeEntryDialog isOpen onClose={closeDialog} projectId={id} />}
      {dialog?.type === 'project' && (
        <ProjectDialog isOpen onClose={closeDialog} project={project} />
      )}

      <ConfirmModal
        isOpen={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        onConfirm={handleDelete}
        title={deleteModal ? t(`${deleteMeta[deleteModal.type].ns}.deleteConfirmTitle`, { ns: 'pages' }) : ''}
        message={deleteModal ? t(`${deleteMeta[deleteModal.type].ns}.deleteConfirmMessage`, { ns: 'pages' }) : ''}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        t={t}
      />
    </div>
  );
};

export default ProjectDetailPage;
